import { Disclosure, DisclosureButton, DisclosurePanel } from '@headlessui/react'
import { Bars3Icon, XMarkIcon } from '@heroicons/react/24/outline'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../context/AuthContext'
import UserMenu from './UserMenu'
import ThemeToggle from './ThemeToggle'
// Mobile user menu is imported inside the Layout component
// instead of at the top level to avoid circular dependencies

const getNavigation = (currentPage, navigateTo) => [
  { 
    name: 'Dashboard', 
    onClick: navigateTo?.dashboard, 
    href: '/', 
    current: currentPage === 'Dashboard' 
  },
  { 
    name: 'Construction', 
    onClick: navigateTo?.construction?.overview, 
    href: '/construction/overview', 
    current: currentPage === 'Construction' 
  }
]

function classNames(...classes) {
  return classes.filter(Boolean).join(' ')
}

export default function Layout({ children, currentPage, navigateTo }) {
  const { isAuthenticated, user } = useAuth();
  const navigate = useNavigate();
  return (
    <>
      <div className="min-h-screen bg-gray-100 dark:bg-gray-900 transition-colors duration-300">
        <div className="bg-gray-800 dark:bg-gray-950 pb-32 transition-colors duration-300">
          <Disclosure as="nav" className="bg-gray-800 dark:bg-gray-950 transition-colors duration-300">
            <div className="mx-auto max-w-7xl sm:px-6 lg:px-8">
              <div className="border-b border-gray-700">
                <div className="flex h-16 items-center justify-between px-4 sm:px-0">
                  <div className="flex items-center">
                    <div className="shrink-0">
                      <img
                        alt="Your Company"
                        src="https://tailwindcss.com/plus-assets/img/logos/mark.svg?color=indigo&shade=500"
                        className="size-8"
                      />
                    </div>
                    <div className="hidden md:block">
                      <div className="ml-10 flex items-baseline space-x-4">
                        {getNavigation(currentPage, navigateTo).map((item) => (
                          <a
                            key={item.name}
                            href={item.href}
                            onClick={item.onClick ? (e) => {
                              e.preventDefault();
                              item.onClick();
                            } : undefined}
                            aria-current={item.current ? 'page' : undefined}
                            className={classNames(
                              item.current
                                ? 'bg-gray-900 text-white'
                                : 'text-gray-300 hover:bg-gray-700 hover:text-white',
                              'rounded-md px-3 py-2 text-sm font-medium',
                              item.onClick ? 'cursor-pointer' : ''
                            )}
                          >
                            {item.name}
                          </a>
                        ))}
                      </div>
                    </div>
                  </div>
                  <div className="hidden md:block">
                    <div className="ml-4 flex items-center md:ml-6">
                      <ThemeToggle />

                      {/* User menu (imported component) */}
                      <UserMenu />
                    </div>
                  </div>
                  <div className="-mr-2 flex md:hidden">
                    {/* Mobile menu button */}
                    <DisclosureButton className="group relative inline-flex items-center justify-center rounded-md bg-gray-800 p-2 text-gray-400 hover:bg-gray-700 hover:text-white focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-gray-800 focus:outline-hidden">
                      <span className="absolute -inset-0.5" />
                      <span className="sr-only">Open main menu</span>
                      <Bars3Icon aria-hidden="true" className="block size-6 group-data-open:hidden" />
                      <XMarkIcon aria-hidden="true" className="hidden size-6 group-data-open:block" />
                    </DisclosureButton>
                  </div>
                </div>
              </div>
            </div>

            <DisclosurePanel className="border-b border-gray-700 md:hidden">
              <div className="space-y-1 px-2 py-3 sm:px-3">
                {getNavigation(currentPage, navigateTo).map((item) => (
                  <DisclosureButton
                    key={item.name}
                    as="a"
                    href={item.href}
                    onClick={item.onClick ? (e) => {
                      e.preventDefault();
                      item.onClick();
                    } : undefined}
                    aria-current={item.current ? 'page' : undefined}
                    className={classNames(
                      item.current ? 'bg-gray-900 text-white' : 'text-gray-300 hover:bg-gray-700 hover:text-white',
                      'block rounded-md px-3 py-2 text-base font-medium',
                      item.onClick ? 'cursor-pointer' : ''
                    )}
                  >
                    {item.name}
                  </DisclosureButton>
                ))}
              </div>
              <div className="border-t border-gray-700 pt-4 pb-3">
                {/* Import and render MobileUserMenu dynamically */}
                {(() => {
                  // Import the component only when needed
                  const MobileUserMenu = require('./MobileUserMenu').default;
                  return <MobileUserMenu />;
                })()}
              </div>
            </DisclosurePanel>
          </Disclosure>
          <header className="py-10">
            <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
              {currentPage !== 'Admin' && currentPage !== 'Settings' && (
                <h1 className="text-3xl font-bold tracking-tight text-white">{currentPage}</h1>
              )}
            </div>
          </header>
        </div>

        <main className="-mt-32">
          <div className="mx-auto max-w-7xl px-4 pb-12 sm:px-6 lg:px-8">
            <div className="rounded-lg bg-white dark:bg-gray-800 shadow-sm transition-colors duration-300 min-h-screen">
              {children}
            </div>
          </div>
        </main>
      </div>
    </>
  )
}